import { AddOutlined, EditOutlined, LinkOutlined } from "@mui/icons-material";
import { Box, Button, Chip, IconButton, Tooltip } from "@mui/material";
import { grey } from "@mui/material/colors";
import PropTypes from "prop-types";
import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { filePreviewSetData } from "../../redux/reducers/filePreviewReducer";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import {
  filterAssociationFields,
  flattenSelectedAssociations,
} from "./config/associationFieldsConfig";

const CHIP_MAX_WIDTH = "185px";
const AssociationsDisplay = ({
  loan,
  associations = {},
  onEdit,
  dense = false,
  enabledFields,
  title = "Associations",
  iconsOnly = false,
  chipMaxWidth,
  compactEmpty = false,
  emptyLabel = "Click to link records.",
  sx,
}) => {
  const dispatch = useDispatch();
  // Linked loan documents open the global file preview (displayed files are
  // always previewable); other record types stay as plain chips.
  const previewFor = (field, rec) =>
    field.key === "loanDocuments" && rec?._id
      ? (e) => {
          e.stopPropagation();
          dispatch(filePreviewSetData(rec));
        }
      : undefined;

  const fields = useMemo(
    () => filterAssociationFields(enabledFields),
    [enabledFields],
  );

  const items = useMemo(
    () => flattenSelectedAssociations(loan, associations, fields),
    [loan, associations, fields],
  );

  const grouped = useMemo(() => {
    const map = new Map();
    for (const f of fields) {
      const ids = associations?.[f.key] || [];
      if (ids.length > 0) map.set(f.key, { field: f, ids });
    }
    return map;
  }, [fields, associations]);

  const total = items.length;

  return (
    <Box
      sx={{
        ".MuiChip-root": {
          fontSize: "10.5px",
        },
        ".MuiSvgIcon-root": {
          fontSize: "13px !important",
        },
        ...sx,
      }}
    >
      {total === 0 ? (
        compactEmpty ? (
          <Tooltip title="Click to link records" arrow>
            <IconButton
              size="small"
              onClick={onEdit}
              sx={{
                height: 24,
                width: 24,
                border: `1px dashed ${grey[300]}`,
                "&:hover": { borderColor: grey[500] },
              }}
            >
              <AddOutlined sx={{ fontSize: 14, color: grey[600] }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Box
            onClick={onEdit}
            role="button"
            sx={{
              cursor: "pointer",
              width: "100%",
              py: 1.5,
              borderRadius: 1,
              border: `1px dashed ${grey[300]}`,
              color: grey[600],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              transition: "all 0.1s",
              "&:hover": {
                bgcolor: grey[50],
                borderColor: grey[400],
              },
            }}
          >
            <AddOutlined sx={{ fontSize: 14 }} />
            <Txt fontSize={12} sx={{ color: grey[600] }}>
              {emptyLabel}
            </Txt>
          </Box>
        )
      ) : dense ? (
        <Flx column g={0.5}>
          {[...grouped.values()].map(({ field, ids }) => {
            const Icon = field.icon;
            const source = field.getSource(loan) || [];
            const previewItems = ids
              .slice(0, 2)
              .map((id) => source.find((x) => x?._id === id))
              .filter(Boolean)
              .map((r) => field.getLabel(r));
            const more = ids.length - previewItems.length;
            return (
              <Flx ac g={1} key={field.key} sx={{ py: 0.25 }}>
                <Flx
                  ac
                  jc="center"
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: 0.75,
                    bgcolor: `${field.color}1A`,
                    color: field.color,
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: "12px !important" }} />
                </Flx>
                <Txt
                  fontSize={11}
                  fw={600}
                  sx={{ color: grey[800], flexShrink: 0 }}
                >
                  {field.label}
                </Txt>
                <Txt fontSize={10} sx={{ color: grey[400] }}>
                  ·
                </Txt>
                <Txt
                  fontSize={11}
                  sx={{
                    color: grey[600],
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {previewItems.join(", ")}
                  {more > 0 && ` +${more}`}
                </Txt>
              </Flx>
            );
          })}
        </Flx>
      ) : (
        <Flx wrap g={0.75}>
          {items.map(({ field, rec }) => {
            const Icon = field.icon;
            const label = field.getLabel(rec);
            const onPreview = previewFor(field, rec);
            if (iconsOnly) {
              return (
                <Tooltip
                  key={`${field.key}:${rec._id}`}
                  title={`${field.label}: ${label}`}
                  arrow
                >
                  <Box
                    onClick={onPreview}
                    role={onPreview ? "button" : undefined}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: `${field.color}14`,
                      border: `1px solid ${field.color}40`,
                      color: field.color,
                      cursor: onPreview ? "pointer" : "default",
                    }}
                  >
                    <Icon sx={{ color: field.color }} />
                  </Box>
                </Tooltip>
              );
            }
            return (
              <Tooltip
                key={`${field.key}:${rec._id}`}
                title={`${field.label}: ${label}`}
                arrow
              >
                <Chip
                  size="small"
                  onClick={onPreview}
                  icon={
                    <Icon
                      sx={{
                        fontSize: 13,
                        color: `${field.color} !important`,
                      }}
                    />
                  }
                  label={label}
                  sx={{
                    height: 24,
                    ...(onPreview && {
                      "&:hover": { bgcolor: `${field.color}29` },
                    }),
                    // maxWidth: 220,
                    maxWidth: chipMaxWidth || CHIP_MAX_WIDTH,
                    bgcolor: `${field.color}14`,
                    border: `1px solid ${field.color}40`,
                    color: grey[900],
                    fontSize: 11,
                    fontWeight: 500,
                    "& .MuiChip-label": {
                      px: 0.75,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                    "& .MuiChip-icon": { ml: 0.5 },
                  }}
                />
              </Tooltip>
            );
          })}
          <Tooltip title="Click to link records" arrow>
            <IconButton
              size="small"
              onClick={onEdit}
              sx={{
                height: 24,
                width: 24,
                border: `1px dashed ${grey[300]}`,
                "&:hover": { borderColor: grey[500] },
              }}
            >
              <AddOutlined sx={{ fontSize: 14, color: grey[600] }} />
            </IconButton>
          </Tooltip>
        </Flx>
      )}
    </Box>
  );
  // return (
  //   <Box
  //     sx={{
  //       border: `1px solid ${grey[200]}`,
  //       borderRadius: 1.5,
  //       bgcolor: "#fff",
  //     }}
  //   >
  //     <Flx
  //       ac
  //       jb
  //       sx={{
  //         px: 1.25,
  //         py: 0.75,
  //         borderBottom: `1px solid ${grey[200]}`,
  //         bgcolor: grey[50],
  //       }}
  //     >
  //       <Flx ac g={0.75}>
  //         <LinkOutlined sx={{ fontSize: 14, color: grey[600] }} />
  //         <Txt
  //           fontSize={11}
  //           fw={600}
  //           sx={{
  //             textTransform: "uppercase",
  //             letterSpacing: "0.08em",
  //             color: grey[700],
  //           }}
  //         >
  //           {title}
  //         </Txt>
  //         {total > 0 && (
  //           <Box
  //             sx={{
  //               px: 0.75,
  //               py: 0.125,
  //               borderRadius: 0.5,
  //               bgcolor: grey[200],
  //             }}
  //           >
  //             <Txt fontSize={10} fw={600} sx={{ color: grey[800] }}>
  //               {total}
  //             </Txt>
  //           </Box>
  //         )}
  //       </Flx>
  //       <Button
  //         size="small"
  //         onClick={onEdit}
  //         startIcon={<EditOutlined sx={{ fontSize: 14 }} />}
  //       >
  //         {total === 0 ? "Add" : "Edit"}
  //       </Button>
  //     </Flx>

  //     <Box sx={{ p: 1.25 }}>
  //       {total === 0 ? (
  //         <Box
  //           onClick={onEdit}
  //           role="button"
  //           sx={{
  //             cursor: "pointer",
  //             width: "100%",
  //             py: 1.5,
  //             borderRadius: 1,
  //             border: `1px dashed ${grey[300]}`,
  //             color: grey[600],
  //             display: "flex",
  //             alignItems: "center",
  //             justifyContent: "center",
  //             gap: 0.75,
  //             transition: "all 0.1s",
  //             "&:hover": {
  //               bgcolor: grey[50],
  //               borderColor: grey[400],
  //             },
  //           }}
  //         >
  //           <AddOutlined sx={{ fontSize: 14 }} />
  //           <Txt fontSize={12} sx={{ color: grey[600] }}>
  //             No associations. Click to link records.
  //           </Txt>
  //         </Box>
  //       ) : dense ? (
  //         <Flx column g={0.5}>
  //           {[...grouped.values()].map(({ field, ids }) => {
  //             const Icon = field.icon;
  //             const source = field.getSource(loan) || [];
  //             const previewItems = ids
  //               .slice(0, 2)
  //               .map((id) => source.find((x) => x?._id === id))
  //               .filter(Boolean)
  //               .map((r) => field.getLabel(r));
  //             const more = ids.length - previewItems.length;
  //             return (
  //               <Flx ac g={1} key={field.key} sx={{ py: 0.25 }}>
  //                 <Flx
  //                   ac
  //                   jc="center"
  //                   sx={{
  //                     width: 20,
  //                     height: 20,
  //                     borderRadius: 0.75,
  //                     bgcolor: `${field.color}1A`,
  //                     color: field.color,
  //                     flexShrink: 0,
  //                   }}
  //                 >
  //                   <Icon sx={{ fontSize: 12 }} />
  //                 </Flx>
  //                 <Txt
  //                   fontSize={11}
  //                   fw={600}
  //                   sx={{ color: grey[800], flexShrink: 0 }}
  //                 >
  //                   {field.label}
  //                 </Txt>
  //                 <Txt fontSize={10} sx={{ color: grey[400] }}>
  //                   ·
  //                 </Txt>
  //                 <Txt
  //                   fontSize={11}
  //                   sx={{
  //                     color: grey[600],
  //                     overflow: "hidden",
  //                     textOverflow: "ellipsis",
  //                     whiteSpace: "nowrap",
  //                     flex: 1,
  //                     minWidth: 0,
  //                   }}
  //                 >
  //                   {previewItems.join(", ")}
  //                   {more > 0 && ` +${more}`}
  //                 </Txt>
  //               </Flx>
  //             );
  //           })}
  //         </Flx>
  //       ) : (
  //         <Flx wrap g={0.75}>
  //           {items.map(({ field, rec }) => {
  //             const Icon = field.icon;
  //             const label = field.getLabel(rec);
  //             return (
  //               <Tooltip
  //                 key={`${field.key}:${rec._id}`}
  //                 title={`${field.label}: ${label}`}
  //                 arrow
  //               >
  //                 <Chip
  //                   size="small"
  //                   icon={
  //                     <Icon
  //                       sx={{
  //                         fontSize: 13,
  //                         color: `${field.color} !important`,
  //                       }}
  //                     />
  //                   }
  //                   label={label}
  //                   sx={{
  //                     height: 24,
  //                     maxWidth: 220,
  //                     bgcolor: `${field.color}14`,
  //                     border: `1px solid ${field.color}40`,
  //                     color: grey[900],
  //                     fontSize: 11,
  //                     fontWeight: 500,
  //                     "& .MuiChip-label": {
  //                       px: 0.75,
  //                       overflow: "hidden",
  //                       textOverflow: "ellipsis",
  //                     },
  //                     "& .MuiChip-icon": { ml: 0.5 },
  //                   }}
  //                 />
  //               </Tooltip>
  //             );
  //           })}
  //           <IconButton
  //             size="small"
  //             onClick={onEdit}
  //             sx={{
  //               height: 24,
  //               width: 24,
  //               border: `1px dashed ${grey[300]}`,
  //               "&:hover": { borderColor: grey[500] },
  //             }}
  //           >
  //             <AddOutlined sx={{ fontSize: 14, color: grey[600] }} />
  //           </IconButton>
  //         </Flx>
  //       )}
  //     </Box>
  //   </Box>
  // );
};

AssociationsDisplay.propTypes = {
  loan: PropTypes.object.isRequired,
  associations: PropTypes.object,
  onEdit: PropTypes.func.isRequired,
  dense: PropTypes.bool,
  enabledFields: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string,
  iconsOnly: PropTypes.bool,
  compactEmpty: PropTypes.bool,
  emptyLabel: PropTypes.string,
};

export default AssociationsDisplay;
